import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Sun, Moon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { LinkedInButton } from "@/components/ui/LinkedInButton";
import { useSettings } from "@/context/SettingsContext";
import { useTheme } from "@/context/ThemeContext";
import { useFetch } from "@/hooks/useFetch";
import { localizedPath, swapLang, pick } from "@/lib/helpers";
import { setLangCookie } from "@/i18n";

const NAV = [
  { key: "home", path: "/" },
  { key: "services", path: "/services", hasDropdown: true },
  { key: "about", path: "/about" },
  { key: "contact", path: "/contact" },
];

function ThemeToggle({ className = "" }) {
  const { theme, toggle, canToggle } = useTheme();
  if (!canToggle) return null;
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle"
      className={`inline-flex items-center justify-center w-11 h-11 border border-current/40 transition-colors hover:text-accent hover:border-accent ${className}`}
    >
      {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
    </button>
  );
}

function LangSwitch({ className = "", onSelect }) {
  const { lang = "en" } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const go = (to) => { setLangCookie(to); i18n.changeLanguage(to); if (onSelect) onSelect(); };
  return (
    <div className={`flex items-center font-techmono text-xs ${className}`} data-testid="lang-switcher">
      {["en", "fr"].map((l, i) => (
        <React.Fragment key={l}>
          {i === 1 && <span className="opacity-40 mx-1">|</span>}
          <Link
            to={swapLang(location.pathname, l)}
            onClick={() => go(l)}
            data-testid={`lang-${l}`}
            aria-current={lang === l ? "true" : undefined}
            className={`uppercase tracking-wider px-1 transition-colors ${lang === l ? "text-accent" : "opacity-80 hover:opacity-100"}`}
          >
            {l}
          </Link>
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * MobileMenu — rendered into a React portal attached to <body> so it is OUTSIDE
 * any transformed/clipped parent stacking context. Uses position: fixed, inset:0,
 * 100dvh (with 100vh fallback), high z-index, internal scroll, and locks body
 * scroll WITHOUT losing the user's scroll position (restored on close).
 */
function MobileMenu({ open, onClose }) {
  const { t } = useTranslation();
  const { lang = "en" } = useParams();
  const { settings } = useSettings();
  const location = useLocation();
  const [accOpen, setAccOpen] = useState(false);
  const { data: services } = useFetch("/services/nav", []);
  const serviceList = services || [];
  const serviceSlug = (s) => (lang === "fr" ? s.slugFr : s.slugEn);

  // Body scroll lock with position restoration. Captures current scrollY,
  // pins <body> with position: fixed and top:-scrollY (prevents layout shift
  // and the iOS rubber-band issue), then restores on close.
  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      // Bypass html { scroll-behavior: smooth } so the scroll snaps back
      // instantly without a visible jump animation.
      const html = document.documentElement;
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, scrollY);
      html.style.scrollBehavior = prevBehavior;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Close on route change (handled by parent firing onClose when location changes)
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (open && lastPath.current !== location.pathname) {
      onClose();
    }
    lastPath.current = location.pathname;
  }, [location.pathname, open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          // True viewport-level overlay; sits above everything, ignores ancestor
          // transforms because it's portaled to <body>.
          style={{
            position: "fixed",
            inset: 0,
            height: "100vh",
            // dvh covers mobile browser chrome (address bar) properly; vh is the
            // fallback for older engines (the height: 100vh above is the fallback).
            // We override via CSS variable trick: just set both — last one wins
            // where supported.
          }}
          className="z-[1000] lg:hidden bg-bg text-fg flex flex-col [height:100dvh] supports-[height:100dvh]:[height:100dvh]"
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label={t("nav.menu")}
        >
          {/* Backdrop (entire surface is solid bg already, but tapping outside
             panel content also closes — we listen on the wrapper) */}
          <div
            className="flex-1 flex flex-col overflow-hidden"
            style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {/* Sticky header inside the menu */}
            <div className="sticky top-0 z-10 bg-bg border-b border-hair">
              <div className="container-x flex items-center justify-between h-20">
                <Logo onClick={onClose} />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("nav.close")}
                  data-testid="mobile-menu-close"
                  className="w-11 h-11 grid place-items-center text-fg hover:text-accent transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Scrollable nav body */}
            <nav
              className="flex-1 overflow-y-auto overscroll-contain container-x py-6"
              aria-label="Mobile"
              data-testid="mobile-menu-nav"
            >
              {NAV.map((item) =>
                item.hasDropdown ? (
                  <div key={item.key} className="border-b border-hair">
                    <button
                      type="button"
                      onClick={() => setAccOpen((o) => !o)}
                      aria-expanded={accOpen}
                      data-testid="mobile-nav-services"
                      className="w-full flex items-center justify-between font-display uppercase tracking-wide text-2xl sm:text-3xl py-4 text-fg"
                    >
                      {t("nav.capabilities")}
                      <ChevronDown className={`w-6 h-6 transition-transform ${accOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {accOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <Link
                            to={localizedPath(lang, "/services")}
                            onClick={onClose}
                            className="block py-2.5 text-accent font-display uppercase tracking-wide"
                          >
                            {t("cta.viewAllServices")}
                          </Link>
                          {serviceList.map((s) => (
                            <Link
                              key={s.id}
                              to={localizedPath(lang, `/services/${serviceSlug(s)}`)}
                              onClick={onClose}
                              data-testid={`mobile-dropdown-service-${serviceSlug(s)}`}
                              className="block py-2.5 text-mut hover:text-accent"
                            >
                              {pick({ en: s.titleEn, fr: s.titleFr }, lang)}
                            </Link>
                          ))}
                          <div className="h-2" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <NavLink
                    key={item.key}
                    to={localizedPath(lang, item.path)}
                    end={item.path === "/"}
                    onClick={onClose}
                    data-testid={`mobile-nav-${item.key}`}
                    className={({ isActive }) =>
                      `block font-display uppercase tracking-wide text-2xl sm:text-3xl py-4 border-b border-hair ${
                        isActive ? "text-accent" : "text-fg"
                      }`
                    }
                  >
                    {t(`nav.${item.key}`)}
                  </NavLink>
                )
              )}

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <LangSwitch onSelect={onClose} />
                <div className="flex items-center gap-3">
                  <ThemeToggle />
                  <LinkedInButton url={settings.linkedinUrl} label={t("nav.linkedin")} />
                </div>
              </div>
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function Header() {
  const { t } = useTranslation();
  const { lang = "en" } = useParams();
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const ddRef = useRef(null);

  const { data: services } = useFetch("/services/nav", []);
  const serviceList = services || [];
  const serviceSlug = (s) => (lang === "fr" ? s.slugFr : s.slugEn);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close dropdown on outside click / escape
  useEffect(() => {
    if (!ddOpen) return undefined;
    const onClick = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false); };
    const onKey = (e) => e.key === "Escape" && setDdOpen(false);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); window.removeEventListener("keydown", onKey); };
  }, [ddOpen]);

  // ── Header state system ──────────────────────────────────────────────
  const solid = scrolled;
  const baseText = solid ? "text-fg" : "text-white";
  const heroShadow = solid ? "" : "drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]";

  const navClass = ({ isActive }) =>
    `font-body text-sm font-medium uppercase tracking-wider transition-colors pb-1 border-b-2 ${
      isActive ? "text-accent border-accent" : `${baseText} hover:text-accent border-transparent`
    }`;

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        solid ? "bg-[color-mix(in_srgb,var(--bg)_90%,transparent)] backdrop-blur-md border-b border-hair shadow-sm" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className={`container-x flex items-center justify-between gap-3 h-20 ${baseText} ${heroShadow}`}>
        <Logo textClass={baseText} forceDark={!solid} className="min-w-0 flex-1 lg:flex-initial" />

        <nav className="hidden lg:flex items-center gap-9" aria-label="Primary">
          {NAV.map((item) =>
            item.hasDropdown ? (
              <div key={item.key} className="relative" ref={ddRef}>
                <button
                  type="button"
                  onClick={() => setDdOpen((o) => !o)}
                  aria-expanded={ddOpen}
                  data-testid="nav-services-dropdown"
                  className={`inline-flex items-center gap-1 font-body text-sm font-medium uppercase tracking-wider transition-colors pb-1 border-b-2 border-transparent ${baseText} hover:text-accent`}
                >
                  {t("nav.capabilities")} <ChevronDown className={`w-4 h-4 transition-transform ${ddOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {ddOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.18 }}
                      className="absolute left-0 top-full mt-3 w-72 bg-surface border border-hair shadow-xl py-2 max-h-[70vh] overflow-y-auto"
                      data-testid="services-dropdown-menu"
                    >
                      <Link
                        to={localizedPath(lang, "/services")}
                        onClick={() => setDdOpen(false)}
                        className="block px-4 py-2.5 text-sm font-display uppercase tracking-wide text-accent hover:bg-surface2"
                      >
                        {t("cta.viewAllServices")}
                      </Link>
                      <div className="h-px bg-hair my-1" />
                      {serviceList.map((s) => (
                        <Link
                          key={s.id}
                          to={localizedPath(lang, `/services/${serviceSlug(s)}`)}
                          onClick={() => setDdOpen(false)}
                          data-testid={`dropdown-service-${serviceSlug(s)}`}
                          className="block px-4 py-2.5 text-sm text-fg hover:text-accent hover:bg-surface2"
                        >
                          {pick({ en: s.titleEn, fr: s.titleFr }, lang)}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink key={item.key} to={localizedPath(lang, item.path)} end={item.path === "/"} className={navClass} data-testid={`nav-${item.key}`}>
                {t(`nav.${item.key}`)}
              </NavLink>
            )
          )}
        </nav>

        <div className={`hidden lg:flex items-center gap-4 ${baseText}`}>
          <LangSwitch />
          <ThemeToggle />
          <LinkedInButton url={settings.linkedinUrl} label={t("nav.linkedin")} className="w-11 h-11" />
        </div>

        <div className={`flex lg:hidden items-center gap-2 shrink-0 ${baseText}`}>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t("nav.menu")}
            aria-expanded={open}
            data-testid="mobile-menu-open"
            className="w-11 h-11 grid place-items-center"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
}
